import { useRltCertificateShowQuery } from "@/services/backend/rlt/certificates/api/certificate-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RefreshCw } from "lucide-react";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { Certificate } from "@/services/backend/rlt/certificates/types/certificate";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { CertificateComponent } from "@/routes/processes/[processId]/rlt/certificates/[certificateId]/_components/certificate-component";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { DocumentPage } from "@/shared/bsm/core/document/components/ui/document-page";

export function RltCertificatePreview({
  certificateId,
}: {
  certificateId: string;
}) {
  const {
    data: certificate,
    isLoading,
    error,
  } = useRltCertificateShowQuery({ id: certificateId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <CertificateDocumentSheet certificate={certificate!} />
    </DocumentSheetContextProvider>
  );
}

function CertificateDocumentSheet({
  certificate,
}: {
  certificate: Certificate;
}) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(certificate!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <CertificatePreview certificate={certificate!} />
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function CertificatePreview({ certificate }: { certificate: Certificate }) {
  return (
    <>
      <CertificateView certificate={certificate} />
      <CertificateActions certificate={certificate} />
    </>
  );
}

function CertificateView({ certificate }: { certificate: Certificate }) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <div className="max-h-[80vh] overflow-auto">
            <CertificateComponent certificateId={certificate.id} />
          </div>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function CertificateActions({ certificate }: { certificate: Certificate }) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(certificate.documentKind)}
        documentNumber={
          <ReferenceNumber referenceNumber={certificate.referenceNumber} />
        }
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={certificate.id}
          documentKind={certificate.documentKind}
          serviceKind={GSMServiceKind.RLT}
          processId={certificate.processId}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
